import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('stationflow_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const login = (email: string, password: string) => api.post('/auth/login', { email, password });
export const getMe = () => api.get('/auth/me');

// Dashboard
export const getDashboard = () => api.get('/dashboard');
export const getVentesMensuelles = (annee: number) => api.get(`/dashboard/ventes-mensuelles/${annee}`);
export const getTopStations = () => api.get('/dashboard/top-stations');
export const getAlertesRecentes = () => api.get('/dashboard/alertes-recentes');
export const getDashboardStation = (stationId: number) => api.get(`/dashboard/station/${stationId}`);

// Stations
export const getStations = (params?: Record<string, string>) => api.get('/stations', { params });
export const getStation = (id: number) => api.get(`/stations/${id}`);
export const createStation = (data: any) => api.post('/stations', data);
export const updateStation = (id: number, data: any) => api.put(`/stations/${id}`, data);
export const getEquipeStation = (id: number) => api.get(`/stations/${id}/equipe`);
export const affecterUser = (stationId: number, data: any) => api.post(`/stations/${stationId}/affecter`, data);
export const retirerAffectation = (stationId: number, userId: number) => api.delete(`/stations/${stationId}/affecter/${userId}`);
export const getObjectifs = (stationId: number, params?: Record<string, string>) => api.get(`/stations/${stationId}/objectifs`, { params });
export const setObjectif = (stationId: number, data: any) => api.post(`/stations/${stationId}/objectifs`, data);

// Releves pompes
export const getReleves = (params?: Record<string, string>) => api.get('/releves', { params });
export const getReleve = (id: number) => api.get(`/releves/${id}`);
export const createReleve = (data: any) => api.post('/releves', data);
export const updateReleve = (id: number, data: any) => api.put(`/releves/${id}`, data);
export const deleteReleve = (id: number) => api.delete(`/releves/${id}`);
export const soumettreReleve = (id: number, data: any) => api.put(`/releves/${id}/soumettre`, data);
export const validerReleve = (id: number) => api.put(`/releves/${id}/valider`);
export const rejeterReleve = (id: number, data: any) => api.put(`/releves/${id}/rejeter`, data);
export const getSyntheseReleves = (params?: Record<string, string>) => api.get('/releves/synthese', { params });

// Journaux
export const getJournaux = (params?: Record<string, string>) => api.get('/journaux', { params });
export const getJournal = (id: number) => api.get(`/journaux/${id}`);
export const createJournal = (data: any) => api.post('/journaux', data);
export const updateJournal = (id: number, data: any) => api.put(`/journaux/${id}`, data);
export const ajouterMouvement = (id: number, data: any) => api.post(`/journaux/${id}/mouvements`, data);
export const updateMouvement = (journalId: number, mouvId: number, data: any) => api.put(`/journaux/${journalId}/mouvements/${mouvId}`, data);
export const deleteMouvement = (journalId: number, mouvId: number) => api.delete(`/journaux/${journalId}/mouvements/${mouvId}`);
export const cloturerJournal = (id: number, data: any) => api.put(`/journaux/${id}/cloturer`, data);
export const demanderOtp = (id: number) => api.post(`/journaux/${id}/demander-otp`);
export const rouvrirJournal = (id: number, data?: any) => api.put(`/journaux/${id}/rouvrir`, data);

// Inventaire
export const getProduits = () => api.get('/inventaire/produits');
export const getCuves = (params?: Record<string, string>) => api.get('/inventaire/cuves', { params });
export const getPompes = (params?: Record<string, string>) => api.get('/inventaire/pompes', { params });
export const getLivraisons = (params?: Record<string, string>) => api.get('/inventaire/livraisons', { params });
export const createLivraison = (data: any) => api.post('/inventaire/livraisons', data);
export const createProduit = (data: any) => api.post('/inventaire/produits', data);
export const updateProduit = (id: number, data: any) => api.put(`/inventaire/produits/${id}`, data);
export const createCuve = (data: any) => api.post('/inventaire/cuves', data);
export const updateCuve = (id: number, data: any) => api.put(`/inventaire/cuves/${id}`, data);
export const createPompe = (data: any) => api.post('/inventaire/pompes', data);
export const updatePompe = (id: number, data: any) => api.put(`/inventaire/pompes/${id}`, data);
export const getMouvementsStock = (params?: Record<string, string>) => api.get('/inventaire/mouvements', { params });
export const createMouvementStock = (data: any) => api.post('/inventaire/mouvements', data);

// Alertes
export const getAlertes = (params?: Record<string, string>) => api.get('/alertes', { params });
export const getAlertesStats = () => api.get('/alertes/stats');
export const createAlerte = (data: any) => api.post('/alertes', data);
export const resoudreAlerte = (id: number, data: any) => api.put(`/alertes/${id}/resoudre`, data);
export const marquerAlerteVue = (id: number) => api.put(`/alertes/${id}/vue`);
export const autoCheckAlertes = () => api.post('/alertes/auto-check');

// Users
export const getUsers = (params?: Record<string, string>) => api.get('/users', { params });
export const getUsersStats = () => api.get('/users/stats');
export const createUser = (data: any) => api.post('/users', data);
export const updateUser = (id: number, data: any) => api.put(`/users/${id}`, data);
export const toggleUserStatus = (id: number) => api.put(`/users/${id}/toggle-status`);
export const resetPassword = (id: number, data: any) => api.put(`/users/${id}/reset-password`, data);

// Profile
export const getProfile = () => api.get('/profile');
export const updateProfile = (data: any) => api.put('/profile', data);
export const changePassword = (data: any) => api.put('/profile/password', data);

// Comptabilite
export const getPlanComptes = (params?: Record<string, string>) => api.get('/comptabilite/plan-comptes', { params });
export const createPlanCompte = (data: any) => api.post('/comptabilite/plan-comptes', data);
export const getEcritures = (params?: Record<string, string>) => api.get('/comptabilite/ecritures', { params });
export const createEcriture = (data: any) => api.post('/comptabilite/ecritures', data);
export const deleteEcriture = (id: number) => api.delete(`/comptabilite/ecritures/${id}`);
export const validerEcriture = (id: number) => api.put(`/comptabilite/ecritures/${id}/valider`);
export const getGrandLivre = (params?: Record<string, string>) => api.get('/comptabilite/grand-livre', { params });
export const getBalance = (params?: Record<string, string>) => api.get('/comptabilite/balance', { params });

// Finances
export const getFinanceKpis = (params?: Record<string, string>) => api.get('/finances/kpis', { params });
export const getTresorerie = (params?: Record<string, string>) => api.get('/finances/tresorerie', { params });
export const getCompteResultat = (params?: Record<string, string>) => api.get('/finances/compte-resultat', { params });
export const getSyntheseJournaliere = (params?: Record<string, string>) => api.get('/finances/synthese-journaliere', { params });
export const getBudgets = (params?: Record<string, string>) => api.get('/finances/budgets', { params });
export const setBudget = (data: any) => api.post('/finances/budgets', data);
export const getBudgetVsRealise = (params?: Record<string, string>) => api.get('/finances/budget-vs-realise', { params });

// Fiscalite
export const getFiscaliteResume = (params?: Record<string, string>) => api.get('/fiscalite/resume', { params });
export const getTvaDetails = (params?: Record<string, string>) => api.get('/fiscalite/tva-details', { params });

// Audit
export const getAuditLogs = (params?: Record<string, string>) => api.get('/audit', { params });
export const getAuditStats = () => api.get('/audit/stats');

// Reporting
export const getComparaisonStations = (params?: Record<string, string>) => api.get('/reporting/comparaison-stations', { params });
export const getVentesProduits = (params?: Record<string, string>) => api.get('/reporting/ventes-produits', { params });
export const getEvolutionJournaliere = (params?: Record<string, string>) => api.get('/reporting/evolution-journaliere', { params });
export const getTracking = (params?: Record<string, string>) => api.get('/reporting/tracking', { params });
export const getTopFlop = (params?: Record<string, string>) => api.get('/reporting/top-flop', { params });
export const getEcarts = (params?: Record<string, string>) => api.get('/reporting/ecarts', { params });
export const getSyntheseGroupe = (params?: Record<string, string>) => api.get('/reporting/synthese-groupe', { params });
export const exportCsv = (params?: Record<string, string>) => api.get('/reporting/export-csv', { params, responseType: 'blob' });

// Analytics
export const getAnalyticsKpis = (params?: Record<string, string>) => api.get('/analytics/kpis-globaux', { params });
export const getTendances = (params?: Record<string, string>) => api.get('/analytics/tendances', { params });
export const getPrevisionsVentes = (params?: Record<string, string>) => api.get('/analytics/previsions-ventes', { params });
export const getPrevisionsStock = (params?: Record<string, string>) => api.get('/analytics/previsions-stock', { params });
export const getAnomalies = (params?: Record<string, string>) => api.get('/analytics/anomalies', { params });
export const getSaisonnalite = (params?: Record<string, string>) => api.get('/analytics/saisonnalite', { params });
export const getRecommandations = (params?: Record<string, string>) => api.get('/analytics/recommandations', { params });

// Depenses & Charges
export const getDepensesStats = (params?: Record<string, string>) => api.get('/depenses/stats', { params });
export const getDepensesCategories = () => api.get('/depenses/categories');
export const createDepenseCategorie = (data: any) => api.post('/depenses/categories', data);
export const getDepensesSynthese = (params?: Record<string, string>) => api.get('/depenses/synthese', { params });
export const getDepensesEvolution = (params?: Record<string, string>) => api.get('/depenses/evolution', { params });
export const getDepenses = (params?: Record<string, string>) => api.get('/depenses', { params });
export const createDepense = (data: any) => api.post('/depenses', data);
export const updateDepense = (id: number, data: any) => api.put(`/depenses/${id}`, data);
export const deleteDepense = (id: number) => api.delete(`/depenses/${id}`);
export const validerDepense = (id: number) => api.put(`/depenses/${id}/valider`);
export const payerDepense = (id: number, data?: any) => api.put(`/depenses/${id}/payer`, data);
export const annulerDepense = (id: number) => api.put(`/depenses/${id}/annuler`);

// Paie
export const getPaieStats = (params?: Record<string, string>) => api.get('/paie/stats', { params });
export const getPaieContrats = (params?: Record<string, string>) => api.get('/paie/contrats', { params });
export const getPaieContrat = (id: number) => api.get(`/paie/contrats/${id}`);
export const createPaieContrat = (data: any) => api.post('/paie/contrats', data);
export const updatePaieContrat = (id: number, data: any) => api.put(`/paie/contrats/${id}`, data);
export const getPaieBulletins = (params?: Record<string, string>) => api.get('/paie/bulletins', { params });
export const getPaieBulletin = (id: number) => api.get(`/paie/bulletins/${id}`);
export const genererBulletin = (data: any) => api.post('/paie/bulletins/generer', data);
export const genererBulletinsMasse = (data: any) => api.post('/paie/bulletins/generer-masse', data);
export const validerBulletin = (id: number) => api.put(`/paie/bulletins/${id}/valider`);
export const payerBulletin = (id: number, data?: any) => api.put(`/paie/bulletins/${id}/payer`, data);
export const downloadBulletinsPdf = (params: Record<string, string>) => api.get('/paie/bulletins/pdf', { params, responseType: 'blob' });
export const downloadBulletinPdf = (id: number) => api.get(`/paie/bulletins/${id}/pdf`, { responseType: 'blob' });
export const getPaiePrimes = (params?: Record<string, string>) => api.get('/paie/primes', { params });
export const createPaiePrime = (data: any) => api.post('/paie/primes', data);
export const deletePaiePrime = (id: number) => api.delete(`/paie/primes/${id}`);
export const getPaieHeuresSup = (params?: Record<string, string>) => api.get('/paie/heures-sup', { params });
export const createPaieHeureSup = (data: any) => api.post('/paie/heures-sup', data);
export const deletePaieHeureSup = (id: number) => api.delete(`/paie/heures-sup/${id}`);
export const getPaieAvances = (params?: Record<string, string>) => api.get('/paie/avances', { params });
export const createPaieAvance = (data: any) => api.post('/paie/avances', data);
export const annulerPaieAvance = (id: number) => api.put(`/paie/avances/${id}/annuler`);
export const getPaieConges = (params?: Record<string, string>) => api.get('/paie/conges', { params });
export const createPaieConge = (data: any) => api.post('/paie/conges', data);
export const approuverConge = (id: number) => api.put(`/paie/conges/${id}/approuver`);
export const refuserConge = (id: number) => api.put(`/paie/conges/${id}/refuser`);
export const getPaieAbsences = (params?: Record<string, string>) => api.get('/paie/absences', { params });
export const createPaieAbsence = (data: any) => api.post('/paie/absences', data);
export const getLivrePaie = (params?: Record<string, string>) => api.get('/paie/livre-paie', { params });
export const getJournalPaie = (params?: Record<string, string>) => api.get('/paie/journal-paie', { params });
export const getDeclarationsSociales = (params?: Record<string, string>) => api.get('/paie/declarations-sociales', { params });
export const getHistoriqueSalarie = (userId: number) => api.get(`/paie/historique-salarie/${userId}`);

// RH (Ressources Humaines)
export const getRhEffectifs = () => api.get('/rh/effectifs');
export const getRhEmployes = (params?: Record<string, string>) => api.get('/rh/employes', { params });
export const getRhEmploye = (id: number) => api.get(`/rh/employes/${id}`);
export const createRhEmploye = (data: any) => api.post('/rh/employes', data);
export const updateRhEmploye = (id: number, data: any) => api.put(`/rh/employes/${id}`, data);
export const createAffectation = (data: any) => api.post('/rh/affectations', data);
export const getAffectations = (userId: number) => api.get(`/rh/affectations/${userId}`);
export const getRhPerformance = (params?: Record<string, string>) => api.get('/rh/performance', { params });
export const getRhStatsStation = (stationId: number) => api.get(`/rh/stats-station/${stationId}`);
export const getRhPompistes = (params?: Record<string, string>) => api.get('/rh/pompistes', { params });

export default api;
